import { useCallback, useMemo } from "react";

import { Flex, Box, Image } from "@chakra-ui/react";
import { sortBy } from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { DeliveryCheckinProductCategory } from "__graphql__/types";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { EppoFeatureFlags } from "app/types/flags";
import { routes } from "config/routes";
import { PrecountCard } from "flows/Activities/components/TransportEquipment/PrecountCard";
import { serializeDeliveryCheckInV2 } from "flows/Activities/models/deliveryCheckIn/serializer";
import { DeliveryCheckInType } from "flows/Activities/models/deliveryCheckIn/types";
import { useGetDeliveryCheckInsForHubDeliveredTodayV2Query } from "flows/Activities/queries/deliveryCheckin/deliveryCheckin.generated";
import { REWE_STAGING_ID, REWE_PROD_ID } from "flows/Activities/utils/constants";
import DeliveryTruckColored from "images/random/delivery-truck-colored.svg";
import DeliveryTruck from "images/random/delivery-truck.svg";
import ReweLogo from "images/random/rewe-logo.png";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { IntlMessageId } from "shared/lang";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { Button } from "ui/Button/Button";
import { Spinner } from "ui/Spinner/Spinner";
import { BodyS, TitleM } from "ui/Typography/Typography";
import { isNotNullNorUndefined, isNullOrUndefined } from "utils/tsHelpers";

function DeliveryHistoryItem({
  id,
  supplier,
  deliveredAt,
  categories,
  numberOfAcceptedRollies,
  deliveryNumber,
  labelForCategory,
}: {
  id: string;
  supplier: { id: string; name: string };
  deliveredAt: Date;
  categories?: DeliveryCheckinProductCategory[];
  numberOfAcceptedRollies?: number;
  deliveryNumber: number;
  labelForCategory: (category: DeliveryCheckinProductCategory) => string;
}) {
  const intl = useIntl();

  const isREWE = supplier && [REWE_STAGING_ID, REWE_PROD_ID].includes(supplier.id);
  const logoImg = isREWE ? ReweLogo : DeliveryTruck;

  return (
    <Flex key={id} bgColor="white" px="s200" py="s300" gap="s300" borderRadius="sm" align="center">
      <Image src={logoImg} borderRadius="sm" />
      <Flex direction="column" gap="s50">
        <TitleM noOfLines={2}>{supplier.name}</TitleM>
        <BodyS>
          {intl.formatMessage(
            { id: "pages.activities.delivery-check-in-history.delivery-number" },
            { n: deliveryNumber.toString() },
          )}
        </BodyS>
        <BodyS>
          {intl.formatMessage(
            {
              id: "pages.activities.delivery-check-in-history.arrival-time",
            },
            {
              arrivalTimePlaceholder: deliveredAt.toLocaleString("de-DE", {
                timeStyle: "short",
                timeZone: "Europe/Berlin",
              }),
            },
          )}
        </BodyS>
        {categories && categories.length > 0 && (
          <BodyS>
            {`${intl.formatMessage({
              id: "pages.activities.delivery-check-in-history.category",
            })}: ${categories.map(labelForCategory).join(", ")}`}
          </BodyS>
        )}
        {!isNullOrUndefined(numberOfAcceptedRollies) && (
          <BodyS>
            {`${intl.formatMessage({
              id: "pages.activities.delivery-check-in-history.rollies_accepted",
            })}: ${numberOfAcceptedRollies}`}
          </BodyS>
        )}
      </Flex>
    </Flex>
  );
}

function DeliveryHistoryItems({
  deliveryHistory,
  isLoading = false,
}: {
  deliveryHistory: DeliveryCheckInType[];
  isLoading?: boolean;
}) {
  const intl = useIntl();
  const deliveryNumber = useCallback(
    (deliveryCheckIn: DeliveryCheckInType): number => {
      if (isNullOrUndefined(deliveryHistory)) {
        return 0;
      }
      return (
        deliveryHistory
          .filter(({ supplier }) => supplier.id === deliveryCheckIn.supplier.id)
          .findIndex(({ id }) => id === deliveryCheckIn.id) + 1
      );
    },
    [deliveryHistory],
  );

  const labelForCategory = useCallback(
    (category: DeliveryCheckinProductCategory): string => {
      const normalizedCategory = category.toLowerCase();
      return intl.formatMessage({
        id: `pages.activities.delivery-check-in-history.category-labels.${normalizedCategory}` as IntlMessageId,
      });
    },
    [intl],
  );

  if (isLoading) return <Spinner alignSelf="center" mb="s200" />;

  if (!deliveryHistory) return null;

  return (
    <>
      {deliveryHistory.map((deliveryCheckIn) => (
        <DeliveryHistoryItem
          key={deliveryCheckIn.id}
          {...deliveryCheckIn}
          deliveryNumber={deliveryNumber(deliveryCheckIn)}
          labelForCategory={labelForCategory}
        />
      ))}
    </>
  );
}

function DeliveryCheckInsForHubDeliveredTodayV2() {
  const { data: deliveryHistoryData, loading: isLoading } =
    useGetDeliveryCheckInsForHubDeliveredTodayV2Query({
      fetchPolicy: "network-only",
    });

  const deliveryCheckins =
    deliveryHistoryData?.getDeliveryCheckInsForHubDeliveredTodayV2.deliveryCheckins;

  const deliveryHistory = useMemo(() => {
    return sortBy(deliveryCheckins?.map(serializeDeliveryCheckInV2), (dci) => dci.deliveredAt);
  }, [deliveryCheckins]);

  return <DeliveryHistoryItems deliveryHistory={deliveryHistory} isLoading={isLoading} />;
}

export function DeliveryCheckInHistory() {
  const navigate = useNavigate();

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);

  const { isFeatureEnabled: isTransportEquipmentFlowEnabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_TRANSPORT_EQUIPMENT_FLOW,
  );

  const goToDeliveryCheckIn = useCallback(() => {
    if (isNotNullNorUndefined(timedActivityInstance)) {
      openOngoingActivityModal();
      return;
    }
    navigate(routes.activities.deliveryCheckIn.root);
  }, [navigate, timedActivityInstance]);

  return (
    <Flex w="full" direction="column" gap="s200" data-testid="delivery-check-in-block">
      <TitleM mt="s100">
        <FormattedMessage id="pages.activities.deliveries.title" />
      </TitleM>

      {isTransportEquipmentFlowEnabled && <PrecountCard />}

      <Flex bgColor="white" px="s200" py="s300" gap="s300" borderRadius="sm">
        <Image src={DeliveryTruckColored} boxSize="48px" borderRadius="full" />
        <Box>
          <BodyS mb="s100">
            <FormattedMessage id="pages.activities.deliveries.receive-and-check.body" />
          </BodyS>
          <Button
            variant="secondary"
            onClick={goToDeliveryCheckIn}
            data-testid="delivery-checkin-cta-button"
          >
            <FormattedMessage id="pages.activities.deliveries.receive-and-check.cta" />
          </Button>
        </Box>
      </Flex>
      <DeliveryCheckInsForHubDeliveredTodayV2 />
    </Flex>
  );
}
